import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DeleteIcon from '@material-ui/icons/Delete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Grid, Divider } from "@material-ui/core";
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../actions";

import MyTextField from '../../utils/MyTextField';
import MyDatePicker from '../../utils/MyDatePicker';
import MyTable from '../../utils/MyTable';
import UnoButton from '../../utils/UnoButton';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import { checkUserIsRole, roles } from '../../utils/user.js';
import { unoTheme } from "../../../assets/styles/unoTheme";
import { InstitutionRatesAPI } from '../../../API/institution_rates';
import { InstituitionAPI } from '../../../API/instituition';

import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },

    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
    },

    main: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
        textAlign: 'left',
        overflowY: 'auto',
    },

    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
        textAlign: 'center',
    },

    warningText: {
        fontSize: '0.8rem',
        color: unoTheme.warningColor || '#e67e22',
        textAlign: 'center',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontStyle: 'italic',
    },

    bottom: {
        display: 'flex',
        alignItems: 'flex-end',
        flexDirection: 'column',
        justifyContent: 'flex-end',

        '& .bottomButtons': {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }
    },

    historySection: {
        marginTop: theme.spacing(3),
        flex: 1,
    },

    historyTable: {
        '& .MuiTableRow-head': {
            '& .MuiTableCell-root': {
                borderBottom: 'none',
                color: unoTheme.defaultTextColor,
                fontWeight: '500',
                textTransform: 'uppercase',
                fontSize: '11px',
                padding: '8px 12px',
            },
        },
    },

    historyRow: {
        '& > *': {
            borderBottom: 'unset',
            fontSize: '0.85rem',
            padding: '6px 12px',
        },
        '&:nth-of-type(odd)': {
            backgroundColor: unoTheme.secondaryTuplaTable,
        },
    },

    rightDivBackdrop: {
        backgroundColor: 'white',
        position: 'absolute',
        opacity: 0.7,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        '& .progress': {
            color: unoTheme.mainColor
        }
    },

    hiddenIcon: {
        visibility: 'hidden',
    },

    selectInstituition: {
        marginBottom: theme.spacing(2),
    },

    divider: {
        margin: theme.spacing(2, 0),
    },
}));

export default function InstitutionRateHistory(props) {

    const classes = useStyles();
    const dispatch = useDispatch();

    const { content } = props;

    const clientOn = useSelector(state => state.clientOn);
    const currentUser = useSelector(state => state.currentUser);

    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Dados da instituição selecionada
    const [institutionName, setInstitutionName] = useState('');
    const [institutionCnpj, setInstitutionCnpj] = useState('');
    const [institutionId, setInstituitionId] = useState(null);

    // Campos do formulário "Nova Taxa"
    const [startDate, setStartDate] = useState(null);
    const [custodyRate, setCustodyRate] = useState('');
    const [brokerageRate, setBrokerageRate] = useState('');

    // Histórico de taxas
    const [rateHistory, setRateHistory] = useState([]);

    // Exclusão de uma taxa do histórico
    const [rateToDelete, setRateToDelete] = useState(undefined);
    const [historyChanged, setHistoryChanged] = useState(false);

    // Lista de instituições (para modo novo cadastro)
    const [instituitions, setInstituitions] = useState([]);
    const [selectedInstituitionId, setSelectedInstituitionId] = useState('');

    const isNew = content?.isNew;
    const clientName = content?.clientOnScreen?.rpps_name || content?.clientOnScreen?.rpps_name_initial || '';

    const canDeleteRate = checkUserIsRole(currentUser, [
        roles.admin,
        roles.advisor,
        roles.superadmin,
        roles.manager
    ]);

    async function loadInstituitions() {
        try {
            const response = await InstituitionAPI.listActivesInstituitions();
            if (response.success) {
                const rows = response.body?.rows ?? response.body ?? [];
                setInstituitions(rows);
            }
        } catch (err) {
            console.error('Erro ao carregar instituições:', err);
        }
    }

    async function loadHistory() {
        if (!content?.clientOnScreen?.id || !institutionId) return;

        try {
            const response = await InstitutionRatesAPI.getHistory(
                content.clientOnScreen.id,
                institutionId
            );
            if (response.success) {
                const rows = response.body?.rows ?? response.body ?? [];
                setRateHistory(rows);
            }
        } catch (err) {
            console.error('Erro ao carregar histórico:', err);
        }
    }

    async function handleSave() {
        setSubmitted(true);

        const resolvedInstitutionId = isNew ? selectedInstituitionId : institutionId;

        if (!resolvedInstitutionId) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'warning',
                message: 'Selecione uma instituição'
            }));
            return;
        }

        if (custodyRate === '' && brokerageRate === '') {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'warning',
                message: 'Informe ao menos uma taxa (Custódia ou Corretagem)'
            }));
            return;
        }

        setOpenBackdrop(true);

        try {
            const data = {
                client_id: content.clientOnScreen.id,
                instituition_id: resolvedInstitutionId,
                custody_rate: custodyRate !== '' ? parseFloat(custodyRate.toString().replace(',', '.')) : null,
                brokerage_rate: brokerageRate !== '' ? parseFloat(brokerageRate.toString().replace(',', '.')) : null,
                start_date: moment.utc(startDate).format('YYYY-MM-DD'),
            };

            const response = await InstitutionRatesAPI.create(data);

            if (response.success) {
                dispatch(allActions.mainAlertActions.setMainAlert({
                    show: true,
                    type: 'success',
                    message: 'Taxa cadastrada com sucesso!'
                }));

                // Notifica para a tela recarregar os dados
                dispatch(allActions.rightContentActions.update());
            } else {
                dispatch(allActions.mainAlertActions.setMainAlert({
                    show: true,
                    type: 'error',
                    message: 'Erro ao cadastrar taxa: ' + (response.body?.message || 'Erro desconhecido')
                }));
            }
        } catch (err) {
            console.error('Erro ao salvar taxa:', err);
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: 'Erro ao cadastrar taxa'
            }));
        }

        setOpenBackdrop(false);
    }

    function formatDate(dateStr) {
        if (!dateStr) return '-';
        return moment.utc(dateStr).format('DD/MM/YYYY');
    }

    function formatPercent(value) {
        if (value === null || value === undefined || value === '') return '-';
        return `${parseFloat(value).toFixed(2).replace('.', ',')}%`;
    }

    function getDeleteRateMessage() {
        if (!rateToDelete) return '';

        return (
            <span>
                {'Tem certeza de que deseja excluir a taxa de '}
                <strong>{institutionName}</strong>
                {' incluída em '}
                <strong>{formatDate(rateToDelete.start_date || rateToDelete.created_at)}</strong>
                {'?'}
                <br />
                {`Custódia: ${formatPercent(rateToDelete.custody_rate)} | Corretagem: ${formatPercent(rateToDelete.brokerage_rate)}`}
            </span>
        );
    }

    async function handleDeleteRateAction(confirmed) {
        const row = rateToDelete;
        setRateToDelete(undefined);

        if (!confirmed || !row) return;

        await doDeleteRate(row);
    }

    async function doDeleteRate(row) {
        setOpenBackdrop(true);

        const response = await InstitutionRatesAPI.remove(row.id);

        if (response.success) {
            setHistoryChanged(true);
            await loadHistory();
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'success',
                message: 'Taxa excluída com sucesso!'
            }));
        } else {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: 'Erro ao excluir taxa: ' + (response.body?.message || 'Erro desconhecido')
            }));
        }

        setOpenBackdrop(false);
    }

    function handleClose() {
        // Se houve exclusão, propaga a atualização para a tela de trás recarregar
        if (historyChanged) {
            dispatch(allActions.rightContentActions.update());
        } else {
            dispatch(allActions.rightContentActions.setRightContent(null));
        }
    }

    useEffect(() => {
        if (isNew) {
            loadInstituitions();
        } else if (content?.data) {
            const data = content.data;
            setInstitutionName(data.instituition_name || data.description || '');
            setInstituitionId(data.instituition_id || data.id);
        }
    }, []);

    useEffect(() => {
        if (institutionId) {
            loadHistory();
        }
    }, [institutionId]);

    useEffect(() => {
        if (selectedInstituitionId) {
            const found = instituitions.find(i => i.id == selectedInstituitionId);
            if (found) {
                setInstitutionName(found.description || found.name || '');
                setInstitutionCnpj(found.cnpj || found.cod || '');
                setInstituitionId(found.id);
            }
        } else {
            setInstitutionCnpj('');
        }
    }, [selectedInstituitionId]);

    const panelTitle = isNew
        ? 'Cadastrar Tx. Instituição'
        : 'Histórico e Adição de Taxas';

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <IconButton className={classes.hiddenIcon}>
                    <ArrowBackIcon />
                </IconButton>

                <div style={{ textAlign: 'center' }}>
                    <Typography variant={'h6'}>
                        {panelTitle}
                    </Typography>
                    {
                        isNew && clientName ? (
                            <Typography variant={'subtitle2'} style={{ color: unoTheme.defaultTextColor, textTransform: 'uppercase' }}>
                                {clientName}
                            </Typography>
                        ) : null
                    }
                    {
                        !isNew && institutionName ? (
                            <Typography variant={'subtitle2'} style={{ color: unoTheme.mainColor, textTransform: 'uppercase' }}>
                                {institutionName}
                            </Typography>
                        ) : null
                    }
                </div>

                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </div>

            <div className={classes.main}>

                {/* ========== MODO NOVO CADASTRO ========== */}
                {isNew ? (
                    <>
                        <MyTextField
                            id="select-instituition"
                            outerlabel="Nome"
                            select
                            value={selectedInstituitionId}
                            onChange={(value) => setSelectedInstituitionId(value)}
                            validator={!selectedInstituitionId && submitted}
                            helperText="Selecione uma instituição"
                            size="small"
                            variant="outlined"
                            fullWidth
                            SelectProps={{ native: true }}
                        >
                            <option value="">Selecione...</option>
                            {instituitions.map((inst) => (
                                <option key={inst.id} value={inst.id}>
                                    {inst.description || inst.name}
                                </option>
                            ))}
                        </MyTextField>

                        <br />

                        <MyTextField
                            id="instituition-cnpj"
                            outerlabel="CNPJ"
                            value={institutionCnpj}
                            size="small"
                            variant="outlined"
                            fullWidth
                            disabled
                            placeholder=""
                        />

                        <br />

                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={4}>
                                <MyTextField
                                    id="custody-rate-new"
                                    outerlabel="Tx. Custódia (%)"
                                    value={custodyRate}
                                    onChange={(value) => setCustodyRate(value)}
                                    placeholder=""
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    mask={["onlyNumbers"]}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <MyTextField
                                    id="brokerage-rate-new"
                                    outerlabel="Corretagem (%)"
                                    value={brokerageRate}
                                    onChange={(value) => setBrokerageRate(value)}
                                    placeholder=""
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    mask={["onlyNumbers"]}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <MyDatePicker
                                    outerlabel="Data da Inclusão"
                                    size="small"
                                    format="DD/MM/YYYY"
                                    inputVariant="outlined"
                                    value={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    fullWidth
                                    autoOk
                                    placeholder="DD/MM/AAAA"
                                    validator={!startDate && submitted}
                                    helperText="Informe a data"
                                />
                            </Grid>
                        </Grid>

                        <div className={classes.bottom}>
                            <div className={'bottomButtons'} style={{ justifyContent: 'flex-end' }}>
                                <UnoButton
                                    onClick={handleSave}
                                    type='primary'
                                    variant="contained">
                                    Salvar Cadastro
                                </UnoButton>
                            </div>
                        </div>
                    </>
                ) : null}

                {/* ========== MODO EDIÇÃO / HISTÓRICO ========== */}
                {!isNew ? (
                    <>
                        <Typography className={classes.sectionTitle} variant="subtitle1">
                            Nova Taxa
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <MyDatePicker
                                    outerlabel="Data de Inclusão"
                                    size="small"
                                    format="DD/MM/YYYY"
                                    inputVariant="outlined"
                                    value={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    fullWidth
                                    autoOk
                                    placeholder="DD/MM/AAAA"
                                    validator={!startDate && submitted}
                                    helperText="Informe a data"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MyTextField
                                    id="custody-rate"
                                    outerlabel="Tx. Custódia (%)"
                                    value={custodyRate}
                                    onChange={(value) => setCustodyRate(value)}
                                    placeholder="0,00"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    mask={["onlyNumbers"]}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MyTextField
                                    id="brokerage-rate"
                                    outerlabel="Corretagem (%)"
                                    value={brokerageRate}
                                    onChange={(value) => setBrokerageRate(value)}
                                    placeholder="0,00"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    mask={["onlyNumbers"]}
                                />
                            </Grid>
                        </Grid>

                        <Typography className={classes.warningText}>
                            Obs.: O cadastro de uma nova taxa irá sobrepor a antiga.
                        </Typography>

                        <div className={classes.bottom}>
                            <div className={'bottomButtons'}>
                                <UnoButton
                                    onClick={handleSave}
                                    type='primary'
                                    variant="contained">
                                    Salvar Cadastro
                                </UnoButton>
                            </div>
                        </div>

                        <div className={classes.historySection}>
                            <Divider className={classes.divider} />
                            <Typography className={classes.sectionTitle} variant="subtitle1">
                                Histórico de Taxas
                            </Typography>

                            <MyTable
                                size="small"
                                stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Instituição</TableCell>
                                        <TableCell align="center">Tx. Custódia (%)</TableCell>
                                        <TableCell align="center">Corretagem (%)</TableCell>
                                        <TableCell align="center">Data de Inclusão</TableCell>
                                        {canDeleteRate ? <TableCell align="center"></TableCell> : null}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rateHistory.length > 0 ? (
                                        rateHistory.map((row, index) => (
                                            <TableRow
                                                key={'rate-history-' + (row.id || index)}
                                                className={classes.historyRow}>
                                                <TableCell align="left">{institutionName}</TableCell>
                                                <TableCell align="center">{formatPercent(row.custody_rate)}</TableCell>
                                                <TableCell align="center">{formatPercent(row.brokerage_rate)}</TableCell>
                                                <TableCell align="center">{formatDate(row.start_date || row.created_at)}</TableCell>
                                                {
                                                    canDeleteRate ?
                                                        <TableCell align="center" style={{ width: '48px' }}>
                                                            <IconButton
                                                                size="small"
                                                                style={{ color: unoTheme.redMoney }}
                                                                onClick={() => setRateToDelete(row)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                        : null
                                                }
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={canDeleteRate ? 5 : 4} align="center" style={{ padding: '16px', color: unoTheme.defaultTextColor }}>
                                                Nenhum registro no histórico
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </MyTable>
                        </div>
                    </>
                ) : null}

            </div>

            {openBackdrop ? (
                <div className={classes.rightDivBackdrop}>
                    <CircularProgress className={'progress'} />
                </div>
            ) : null}

            <ConfirmActionModal
                open={rateToDelete !== undefined}
                title={'Excluir taxa'}
                message={getDeleteRateMessage()}
                confirmText={'Excluir'}
                handleAction={(confirmed) => handleDeleteRateAction(confirmed)} />
        </div>
    );
}